<script setup lang="ts">
import type { BasicImage, Image } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import { onMounted, toValue, watchEffect } from 'vue';
import SectionLoadingMessage from '../components/SectionLoadingMessage.vue';
import SectionMessage from '../components/SectionMessage.vue';
import useProjectImages from './composables/useProjectImages';
import ProjectImagePickerItem from './ProjectImageItem.vue';
import SectionErrorMessage from '../components/SectionErrorMessage.vue';

const image = defineModel<BasicImage>('modelValue');
const isValid = defineModel<boolean>('isValid');

const { installedImage = null, forcePreselect = false } = defineProps<{
  installedImage?: Image;
  forcePreselect?: boolean;
}>();

watchEffect(() => {
  isValid.value = image.value != null;
});

const handleProjectImageSelect = (img: BasicImage) => {
  image.value = img;
};

const { data: projectImages, isLoading, fetch, findProjectImage, error } = useProjectImages();

const preselectProjectImage = () => {
  if (projectImages.value.length === 0) {
    image.value = null;
    return;
  }

  // resync the image if it's already selected
  if (image.value != null) {
    const img = findProjectImage(image.value);
    if (img != null) {
      image.value = img;
      return;
    }
  }

  // if we have a preselected image, try to find it in the list
  const installedImageValue = toValue(installedImage);
  if (installedImageValue != null) {
    const img = findProjectImage(installedImageValue);

    if (img != null) {
      image.value = img;
      return;
    }
  }

  // otherwise preselect the first image
  image.value = projectImages.value[0];
  return;
};

onMounted(async () => {
  if (projectImages.value.length === 0) {
    await fetch(true);
  }

  if (forcePreselect) {
    preselectProjectImage();
  } else {
    image.value = null;
  }
});
</script>

<template>
  <SectionLoadingMessage v-if="isLoading">Loading project images ...</SectionLoadingMessage>

  <SectionErrorMessage
    v-else-if="error != null"
    :error="error"
    v-playwright="'nodes-create-image-project-error'"
  >
    Error while loading project images
  </SectionErrorMessage>

  <SectionMessage v-else-if="projectImages.length === 0">
    No project images uploaded yet.
  </SectionMessage>

  <div
    v-else
    class="tw-mt-4 tw-grid tw-auto-rows-[1fr] tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-4"
    v-playwright="'nodes-create-image-project-grid'"
  >
    <ProjectImagePickerItem
      v-for="(img, index) in projectImages"
      :key="img.id"
      class="tw-w-full"
      :image="img"
      :is-selected="image != null && img.id === image.id"
      v-playwright="`nodes-create-image-project-item-${index}`"
      :is-installed="installedImage != null && installedImage.id === img.id"
      @image-selected="handleProjectImageSelect(img)"
    />
  </div>
</template>

<style scoped></style>
